import React from 'react';

import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import _ from 'es-toolkit/compat';

import { Nullable } from '@clearverify/types';
import { numberWithCommas } from '@clearverify/utils';

import { LastUploadRolesResponse } from 'src/api';
import { ChartBase, ChartOptions } from 'src/components/chart';
import { DashboardPalette } from 'src/constants/dashboard';
import { useDashboardPrintView } from 'src/hooks/dashboard';
import { formatLastUploadDate, formatLastUploadRecords } from 'src/utils/dashboard';

import { DashboardChartWrapper } from '../DashboardChartWrapper';

import { DASHBOARD_ACTIVE_RECORDS_COLORS } from './constants';

type DashboardRecordsActivityBlockProps = {
  dataset: Array<{ name: string; value: number }>;
  counts: { total: number; active: number; inactive: number };
  lastUploadRoles?: Nullable<Pick<LastUploadRolesResponse, 'rolesCount' | 'uploadedAt'>>;
  loading?: boolean;
};

export const DashboardRecordsActivityBlock: React.FC<DashboardRecordsActivityBlockProps> = ({
  dataset,
  counts,
  lastUploadRoles,
  loading,
}) => {
  const { isPrintView } = useDashboardPrintView();

  const isDatasetEmpty = !counts.total && !loading;

  // Empty when the load carried no role changes — then only its date is shown.
  const lastUploadRecords = lastUploadRoles
    ? formatLastUploadRecords(lastUploadRoles.rolesCount)
    : '';

  const pieChartLegend = React.useMemo(
    () => ({
      orient: 'vertical' as const,
      left: 'center' as const,
      right: 'center' as const,
      top: 'center' as const,
      bottom: 'center' as const,
      formatter(legendName: string) {
        const legendItem = dataset.find(item => item.name === legendName) ?? {
          name: legendName,
          value: 0,
        };
        const { value } = legendItem;
        const correctPercent = _.round((Number(value) / counts.total) * 100, 2);

        return `${legendName} - ${value} (${correctPercent}%)`;
      },
      itemWidth: 1,
      data: dataset.map((item, index) => ({
        name: item.name,
        icon: 'none' as const,
        textStyle: {
          fontSize: 14,
          backgroundColor: DASHBOARD_ACTIVE_RECORDS_COLORS[index],
          color: 'black' as const,
          padding: 8,
          borderRadius: 4,
          width: 150,
          overflow: 'break' as const,
        },
      })),
    }),
    [dataset, counts],
  );

  const options = React.useMemo(
    (): ChartOptions => ({
      tooltip: {
        trigger: 'item',
        showContent: true,
      },
      ...(isPrintView ? { legend: pieChartLegend, animation: false } : {}),
      series: loading
        ? [
            {
              type: 'pie',
              color: DashboardPalette.slate50,
              radius: ['70%', '95%'],
              label: {
                show: false,
              },
            },
          ]
        : [
            {
              type: 'pie',
              radius: ['70%', '95%'],
              left: '10%',
              right: '10%',
              minAngle: 5,
              avoidLabelOverlap: true,
              label: {
                show: false,
              },
              itemStyle: {
                color(params) {
                  const color = DASHBOARD_ACTIVE_RECORDS_COLORS[params.dataIndex];
                  return color ?? DashboardPalette.slate50;
                },
              },
              tooltip: {
                // for some reason instead of having active with/without accounts segments
                // we have all active and active with accounts, with the latter being a subset of the former
                // that's a part of the reason why we have to do this
                formatter(params) {
                  const { value } = params.value as { value: string };
                  const correctPercent = _.round((Number(value) / counts.total) * 100, 2);

                  return `${params.name} - ${numberWithCommas(value)} (${correctPercent}%)`;
                },
                confine: true,
              },
            },
          ],
      dataset: [
        {
          source: dataset,
        },
      ],
    }),
    [dataset, isPrintView, loading, pieChartLegend, counts],
  );

  return (
    <Paper sx={{ py: 2.5, px: 3, borderRadius: 2, overflow: 'hidden' }} variant="outlined">
      <Stack height="100%" spacing={2}>
        <Box>
          <Typography variant="subtitle1">Active / Inactive Records</Typography>
          <Typography variant="body1" color="text.secondary">
            This chart shows the distribution of records across statuses. Active and inactive
            distinctions are indicated by color, helping you understand the current composition of
            the data set.
          </Typography>
          {/* Only present for a single-org filter, and only once MDM reports a successful load. */}
          {lastUploadRoles ? (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Latest data received: {formatLastUploadDate(lastUploadRoles.uploadedAt)}
              {lastUploadRecords ? ` · ${lastUploadRecords}` : ''}
            </Typography>
          ) : null}
        </Box>
        <Divider />
        <React.Fragment>
          {!isDatasetEmpty && (
            <Stack direction="row" spacing={1} alignSelf="center">
              {(
                [
                  { label: 'Total', value: counts.total },
                  { label: 'Active', value: counts.active },
                  { label: 'Inactive', value: counts.inactive },
                ] as const
              ).map(({ label, value }) => (
                <Typography
                  key={label}
                  variant="body2"
                  sx={{
                    px: 1.5,
                    py: 1,
                    display: 'inline-block',
                    backgroundColor: 'action.focus',
                    borderRadius: '8px',
                  }}
                >
                  {label}: {numberWithCommas(value)}
                </Typography>
              ))}
            </Stack>
          )}

          <Box height="100%" width={isPrintView ? '500px' : 'auto'}>
            <DashboardChartWrapper
              isPrintView={isPrintView}
              wrapperWidth={500}
              isDatasetEmpty={isDatasetEmpty}
            >
              <ChartBase option={options} style={{ height: 384 }} />
            </DashboardChartWrapper>
          </Box>
        </React.Fragment>
      </Stack>
    </Paper>
  );
};
